import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  CancelTokenSource
} from 'axios'
import { API_CONFIG } from '@config/api.config'
import { v4 as uuidv4 } from 'uuid'

// API Response Types
export interface ApiResponse<T = unknown> {
  data: T
  message?: string
  status: number
}

export interface ApiError {
  message: string
  code?: string
  status: number
  errors?: Record<string, string[]>
  requestId?: string
}

interface AxiosRequestConfigWithMetadata extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: Date
    requestId: string
    retryCount: number
    offline?: boolean
  }
}

interface AxiosErrorResponse {
  message?: string
  code?: string
  errors?: Record<string, string[]>
}

// Request Queue for Rate Limiting
class RequestQueue {
  private queue: Array<() => Promise<any>> = []
  private running = 0
  private maxConcurrent = parseInt(import.meta.env.VITE_MAX_CONCURRENT_REQUESTS || '5')
  private throttleMs = parseInt(import.meta.env.VITE_API_REQUEST_THROTTLE_MS || '100')
  private lastRequestTime = 0

  async add<T>(request: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          // Apply throttling
          const now = Date.now()
          const timeSinceLastRequest = now - this.lastRequestTime
          if (timeSinceLastRequest < this.throttleMs) {
            await new Promise(r => setTimeout(r, this.throttleMs - timeSinceLastRequest))
          }
          this.lastRequestTime = Date.now()

          const result = await request()
          resolve(result)
        } catch (error) {
          reject(error)
        }
      })
      this.processQueue()
    })
  }

  private async processQueue() {
    if (this.running >= this.maxConcurrent || this.queue.length === 0) {
      return
    }

    this.running++
    const request = this.queue.shift()
    if (request) {
      await request()
      this.running--
      this.processQueue()
    }
  }
}

// Request Deduplication
class RequestDeduplicator {
  private pendingRequests = new Map<string, Promise<any>>()

  async deduplicate<T>(
    key: string,
    request: () => Promise<T>
  ): Promise<T> {
    const pending = this.pendingRequests.get(key)
    if (pending) {
      return pending
    }

    const promise = request().finally(() => {
      this.pendingRequests.delete(key)
    })

    this.pendingRequests.set(key, promise)
    return promise
  }

  generateKey(config: AxiosRequestConfig): string {
    const method = config.method || 'get'
    const url = config.url || ''
    const params = JSON.stringify(config.params || {})
    const data = JSON.stringify(config.data || {})
    return `${method}:${url}:${params}:${data}`
  }
}

// Network Status Monitor
class NetworkMonitor {
  private online = navigator.onLine
  private listeners: Array<(online: boolean) => void> = []

  constructor() {
    window.addEventListener('online', () => this.setStatus(true))
    window.addEventListener('offline', () => this.setStatus(false))
  }

  private setStatus(online: boolean) {
    this.online = online
    this.listeners.forEach(listener => listener(online))
  }

  isOnline(): boolean {
    return this.online
  }

  addListener(listener: (online: boolean) => void) {
    this.listeners.push(listener)
  }

  removeListener(listener: (online: boolean) => void) {
    this.listeners = this.listeners.filter(l => l !== listener)
  }
}

// Offline Queue for requests made while offline
class OfflineQueue {
  private queue: Array<{
    config: AxiosRequestConfig
    resolve: (value: any) => void
    reject: (reason?: any) => void
  }> = []

  add(config: AxiosRequestConfig): Promise<any> {
    return new Promise((resolve, reject) => {
      this.queue.push({ config, resolve, reject })
      this.saveToLocalStorage()
    })
  }

  async flush(apiClient: AxiosInstance) {
    const items = [...this.queue]
    this.queue = []
    this.saveToLocalStorage()

    for (const item of items) {
      try {
        const response = await apiClient.request(item.config)
        item.resolve(response)
      } catch (error) {
        item.reject(error)
      }
    }
  }

  private saveToLocalStorage() {
    try {
      localStorage.setItem('offlineQueue', JSON.stringify(
        this.queue.map(item => ({
          config: {
            method: item.config.method,
            url: item.config.url,
            data: item.config.data,
            params: item.config.params
          }
        }))
      ))
    } catch (e) {
      console.warn('Failed to save offline queue to localStorage', e)
    }
  }

  loadFromLocalStorage() {
    try {
      const stored = localStorage.getItem('offlineQueue')
      if (stored) {
        const items = JSON.parse(stored)
        // Note: We can't restore the promises, so these would need to be re-executed
        return items
      }
    } catch (e) {
      console.warn('Failed to load offline queue from localStorage', e)
    }
    return []
  }
}

// Request Cancellation Manager
class CancellationManager {
  private tokens = new Map<string, CancelTokenSource>()

  createToken(key: string): CancelTokenSource {
    const existing = this.tokens.get(key)
    if (existing) {
      existing.cancel('Request superseded')
    }

    const source = axios.CancelToken.source()
    this.tokens.set(key, source)
    return source
  }

  cancel(key: string, reason?: string) {
    const token = this.tokens.get(key)
    if (token) {
      token.cancel(reason || 'Request cancelled')
      this.tokens.delete(key)
    }
  }

  cancelAll(reason?: string) {
    this.tokens.forEach(token => {
      token.cancel(reason || 'All requests cancelled')
    })
    this.tokens.clear()
  }
}

// Performance Tracker
class PerformanceTracker {
  private metrics: Array<{
    url: string
    method: string
    duration: number
    timestamp: Date
    status: number
  }> = []

  track(config: AxiosRequestConfigWithMetadata, response: AxiosResponse | undefined, error?: any) {
    if (!config.metadata) return

    const duration = new Date().getTime() - config.metadata.startTime.getTime()
    const metric = {
      url: config.url || '',
      method: config.method || 'get',
      duration,
      timestamp: new Date(),
      status: response?.status || error?.response?.status || 0
    }

    this.metrics.push(metric)

    // Keep only last 100 metrics
    if (this.metrics.length > 100) {
      this.metrics.shift()
    }

    // Log in development mode
    if (import.meta.env.VITE_ENV === 'development' || import.meta.env.VITE_LOG_LEVEL === 'debug') {
      console.debug(`[API] ${metric.method.toUpperCase()} ${metric.url} - ${metric.duration}ms - Status: ${metric.status}`)
    }
  }

  getMetrics() {
    return [...this.metrics]
  }

  getAverageResponseTime(url?: string): number {
    const relevant = url
      ? this.metrics.filter(m => m.url === url)
      : this.metrics

    if (relevant.length === 0) return 0

    const sum = relevant.reduce((acc, m) => acc + m.duration, 0)
    return sum / relevant.length
  }
}

// Exponential Backoff Calculator
function calculateBackoffDelay(retryCount: number, baseDelay: number = 1000): number {
  const maxDelay = 30000 // 30 seconds max
  const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay)
  // Add jitter to avoid thundering herd
  const jitter = Math.random() * 0.3 * delay
  return Math.floor(delay + jitter)
}

// Initialize services
const requestQueue = new RequestQueue()
const requestDeduplicator = new RequestDeduplicator()
const networkMonitor = new NetworkMonitor()
const offlineQueue = new OfflineQueue()
const cancellationManager = new CancellationManager()
const performanceTracker = new PerformanceTracker()

// Create Axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
})

// Request Interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Add request ID for tracing
    const requestId = uuidv4()
    config.headers['X-Request-ID'] = requestId

    // AML-Guard Token Authentication
    // Priority: 1. localStorage token (for session), 2. ENV token (for initial API calls)
    const token = localStorage.getItem('access_token') || API_CONFIG.auth.token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add metadata for tracking
    ;(config as AxiosRequestConfigWithMetadata).metadata = {
      startTime: new Date(),
      requestId,
      retryCount: 0,
      offline: !networkMonitor.isOnline()
    }

    // Log request in development mode
    if (import.meta.env.VITE_ENV === 'development' || import.meta.env.VITE_ENV === 'demo') {
      console.debug(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        requestId,
        params: config.params,
        data: config.data
      })
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response Interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const config = response.config as AxiosRequestConfigWithMetadata

    // Track performance
    performanceTracker.track(config, response)

    // Log response in development mode
    if (import.meta.env.VITE_ENV === 'development') {
      console.debug(`[API Response] ${config.method?.toUpperCase()} ${config.url}`, {
        requestId: config.metadata?.requestId,
        status: response.status,
        data: response.data
      })
    }

    return response
  },
  async (error: AxiosError<AxiosErrorResponse>) => {
    const originalRequest = error.config as (AxiosRequestConfigWithMetadata & { _retry?: boolean }) | undefined

    if (!originalRequest) {
      return Promise.reject(error)
    }

    // Track performance even for errors
    performanceTracker.track(originalRequest, undefined, error)

    // Handle network errors with offline mode
    if (!error.response && !networkMonitor.isOnline()) {
      if (import.meta.env.VITE_FEATURE_OFFLINE_MODE === 'true') {
        console.info('Request queued for offline execution')
        return offlineQueue.add(originalRequest)
      }
    }

    // Don't retry CORS errors (status 0 typically indicates CORS or network issues)
    if (error.response?.status === 0 || !error.response) {
      return Promise.reject(error)
    }

    // Retry logic with exponential backoff
    const maxRetries = parseInt(import.meta.env.VITE_API_MAX_RETRIES || '3')
    const retryableStatuses = [408, 429, 500, 502, 503, 504]

    if (
      !originalRequest._retry &&
      originalRequest.metadata &&
      originalRequest.metadata.retryCount < maxRetries &&
      error.response &&
      retryableStatuses.includes(error.response.status)
    ) {
      originalRequest.metadata.retryCount++
      originalRequest._retry = true

      const delay = calculateBackoffDelay(
        originalRequest.metadata.retryCount,
        parseInt(import.meta.env.VITE_API_RETRY_DELAY || '1000')
      )

      console.info(`Retrying request (attempt ${originalRequest.metadata.retryCount}/${maxRetries}) after ${delay}ms`)

      await new Promise(resolve => setTimeout(resolve, delay))

      // Reset the retry flag for the next attempt
      originalRequest._retry = false

      return apiClient(originalRequest)
    }

    // Handle 401 Unauthorized - Token Refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refresh_token')

        if (!refreshToken) {
          // No refresh token, redirect to login
          window.location.href = '/login'
          return Promise.reject(error)
        }

        // Attempt token refresh
        const response = await axios.post(`${API_CONFIG.baseURL}/auth/refresh`, {
          refresh_token: refreshToken,
        })

        const { access_token, refresh_token: newRefreshToken } = response.data

        // Save new tokens
        localStorage.setItem('access_token', access_token)
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken)
        }

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors
    const responseData = error.response?.data
    const apiError: ApiError = {
      message: responseData?.message || error.message || 'An error occurred',
      code: responseData?.code || error.code,
      status: error.response?.status || 500,
      errors: responseData?.errors,
      requestId: originalRequest.metadata?.requestId
    }

    console.error('[API Error]', {
      ...apiError,
      url: originalRequest.url,
      method: originalRequest.method
    })

    return Promise.reject(apiError)
  }
)

// Listen for network status changes
networkMonitor.addListener((online) => {
  if (online && import.meta.env.VITE_FEATURE_OFFLINE_MODE === 'true') {
    console.info('Network connection restored, flushing offline queue')
    offlineQueue.flush(apiClient)
  }
})

// Enhanced API Methods with request queue and deduplication
export const api = {
  get: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig & { dedupe?: boolean; cancelKey?: string }
  ): Promise<AxiosResponse<T>> => {
    const { dedupe, cancelKey, ...axiosConfig } = config || {}

    // Handle request cancellation
    if (cancelKey) {
      const source = cancellationManager.createToken(cancelKey)
      axiosConfig.cancelToken = source.token
    }

    const request = () => apiClient.get<T>(url, axiosConfig)

    // Apply deduplication if requested
    if (dedupe) {
      const key = requestDeduplicator.generateKey({ ...axiosConfig, url, method: 'get' })
      return requestDeduplicator.deduplicate(key, () => requestQueue.add(request))
    }

    return requestQueue.add(request)
  },

  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig & { cancelKey?: string }
  ): Promise<AxiosResponse<T>> => {
    const { cancelKey, ...axiosConfig } = config || {}

    if (cancelKey) {
      const source = cancellationManager.createToken(cancelKey)
      axiosConfig.cancelToken = source.token
    }

    return requestQueue.add(() => apiClient.post<T>(url, data, axiosConfig))
  },

  put: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig & { cancelKey?: string }
  ): Promise<AxiosResponse<T>> => {
    const { cancelKey, ...axiosConfig } = config || {}

    if (cancelKey) {
      const source = cancellationManager.createToken(cancelKey)
      axiosConfig.cancelToken = source.token
    }

    return requestQueue.add(() => apiClient.put<T>(url, data, axiosConfig))
  },

  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig & { cancelKey?: string }
  ): Promise<AxiosResponse<T>> => {
    const { cancelKey, ...axiosConfig } = config || {}

    if (cancelKey) {
      const source = cancellationManager.createToken(cancelKey)
      axiosConfig.cancelToken = source.token
    }

    return requestQueue.add(() => apiClient.patch<T>(url, data, axiosConfig))
  },

  delete: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig & { cancelKey?: string }
  ): Promise<AxiosResponse<T>> => {
    const { cancelKey, ...axiosConfig } = config || {}

    if (cancelKey) {
      const source = cancellationManager.createToken(cancelKey)
      axiosConfig.cancelToken = source.token
    }

    return requestQueue.add(() => apiClient.delete<T>(url, axiosConfig))
  },

  // Utility methods
  cancel: (key: string, reason?: string) => {
    cancellationManager.cancel(key, reason)
  },

  cancelAll: (reason?: string) => {
    cancellationManager.cancelAll(reason)
  },

  getMetrics: () => {
    return performanceTracker.getMetrics()
  },

  getAverageResponseTime: (url?: string) => {
    return performanceTracker.getAverageResponseTime(url)
  },

  isOnline: () => {
    return networkMonitor.isOnline()
  },

  onNetworkChange: (callback: (online: boolean) => void) => {
    networkMonitor.addListener(callback)
    return () => networkMonitor.removeListener(callback)
  }
}

export default apiClient